import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { BarChart2, TrendingUp, FileText, RefreshCw, Sparkles } from 'lucide-react'
import { apiFetch } from '../../config/api'
import { type PageStat, StatTable } from './statistikShared'

export default function DashboardStatistik() {
  const [pages, setPages] = useState<PageStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  const fetchStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/analytics/pages', {
        headers: { Authorization: getAuthToken() },
      })
      if (!res.ok) {
        if (res.status === 401) return
        throw new Error('Statistiken konnten nicht geladen werden.')
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.pages)) {
        setPages(
          data.pages.map((p: { path: string; today: string | number; last7d: string | number }) => ({
            path: p.path || '/',
            today: String(p.today ?? 0),
            last7d: String(p.last7d ?? 0),
          }))
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const totalToday = pages.reduce((sum, p) => sum + parseInt(p.today, 10) || 0, 0)
  const total7d = pages.reduce((sum, p) => sum + parseInt(p.last7d, 10) || 0, 0)
  const max7d = Math.max(1, ...pages.map((p) => parseInt(p.last7d, 10) || 0))

  const other = useMemo(() => {
    return pages.filter((p) => {
      const path = p.path || ''
      return !path.startsWith('/partys') && !path.startsWith('/brunch')
    })
  }, [pages])

  return (
    <div className="w-full min-w-0 max-w-full">
      <motion.div
        className="mb-6 sm:mb-8 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">
              Statistiken
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Gesamtübersicht und alle anderen Seiten.
            </p>
          </div>
          <motion.button
            onClick={fetchStats}
            disabled={loading}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </motion.button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 md:mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-5 sm:p-6 rounded-xl sm:rounded-xl bg-white border border-zinc-200"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-zinc-100">
              <Sparkles className="w-5 h-5 text-zinc-700" />
            </div>
            <span className="text-zinc-500 text-xs sm:text-sm font-medium">Heute</span>
          </div>
          <p className="text-3xl sm:text-4xl font-bold text-zinc-950">{loading ? '—' : totalToday}</p>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1">Seitenaufrufe gesamt</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-5 sm:p-6 rounded-xl sm:rounded-xl bg-white border border-zinc-200"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-zinc-100">
              <TrendingUp className="w-5 h-5 text-zinc-700" />
            </div>
            <span className="text-zinc-500 text-xs sm:text-sm font-medium">Letzte 7 Tage</span>
          </div>
          <p className="text-3xl sm:text-4xl font-bold text-zinc-950">{loading ? '—' : total7d}</p>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1">Seitenaufrufe gesamt</p>
        </motion.div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </motion.div>
      )}

      {/* Alle anderen Seiten */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-xl sm:rounded-xl bg-white border border-zinc-200 overflow-hidden min-w-0"
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-200 flex items-center gap-3">
          <FileText className="w-5 h-5 text-zinc-700 shrink-0" />
          <h2 className="text-base font-semibold text-zinc-950 truncate">Alle anderen Seiten</h2>
        </div>

        {loading ? (
          <div className="p-8 sm:p-12 text-center text-zinc-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
            <p>Statistiken werden geladen …</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="p-8 sm:p-12 text-center text-zinc-500">
            <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Noch keine Aufrufdaten vorhanden.</p>
            <p className="text-sm mt-1">Sobald Besucher die Website nutzen, erscheinen hier die Zahlen.</p>
          </div>
        ) : other.length === 0 ? (
          <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
            Keine weiteren Seiten mit Aufrufen.
          </div>
        ) : (
          <div className="overflow-x-auto min-w-0 w-full">
            <StatTable rows={other} maxBar={max7d} startIndex={0} />
          </div>
        )}
      </motion.div>
    </div>
  )
}
